<style scoped>
/* The H3 resolution control, as the last section of the layers pane. The lab's
   MapLabLadderControls card owns the same slider for the lab page; this is the
   workbench's version of it — the same offset and the same ladder semantics,
   reading its two numbers from props instead of the lab store. It is a section
   of the pane rather than the on-map panel it used to be, so it takes the
   pane's separator and type scale and no card of its own. */
.h3-res {
	margin-top: 8px;
	padding-top: 8px;
	border-top: 1px solid #f1f3f5;
}

.h3-res-head {
	display: flex;
	align-items: baseline;
	gap: 6px;
}

.h3-res-title {
	flex: 1 1 auto;
	font-weight: 600;
}

.h3-res-offset {
	flex: 0 0 auto;
	font-weight: 600;
	font-variant-numeric: tabular-nums;
	color: #0f172a;
}

.h3-res-track {
	display: block;
	width: 100%;
	margin: 4px 0 0;
}

.h3-res-now {
	margin: 4px 0 0;
	font-size: 0.75rem;
}

.h3-res-res {
	font-weight: 700;
}

.h3-res-note {
	margin: 4px 0 0;
	font-size: 0.75rem;
	color: #6c757d;
}

/* A rung the offset asked to be finer than a zoom can draw: the grid would come
   back missing hexagons, so the ladder stops there and the section says so. */
.h3-res-note--warn {
	color: #b45309;
}
</style>

<template>
	<div class="h3-res">
		<div class="h3-res-head">
			<span class="h3-res-title">Hex resolution</span>
			<span class="h3-res-offset">{{ signedOffset }}</span>
		</div>
		<input
			class="h3-res-track"
			type="range"
			:min="LADDER_OFFSET_MIN"
			:max="LADDER_OFFSET_MAX"
			step="1"
			:value="clampedOffset"
			:title="`Shift every H3 layer's resolution by ${signedOffset} steps: ${LADDER_OFFSET_MIN} is coarser, ${LADDER_OFFSET_MAX} finer`"
			aria-label="H3 resolution offset"
			@input="onOffset"
		/>
		<p class="h3-res-now">
			Drawn at <span class="h3-res-res">R{{ resNow }}</span> —
			{{ px(pxNow) }} px across a hexagon.
		</p>
		<p v-if="capNote" class="h3-res-note h3-res-note--warn">{{ capNote }}</p>
	</div>
</template>

<script setup lang="ts">
// The one H3 resolution slider, as the layers pane's last section: it moves
// EVERY H3 layer at once (they all read the same ladder), so the parent holds
// one offset and this is its control. It is deliberately self-contained — no
// store, no fetch — because the number it shows is a function of the offset and
// the zoom it is handed.
//
// The semantics are the lab's, mirrored rather than re-derived: the offset is
// clamped to its range and rounded, the ladder is `ladderForOffset` (H3-clamped,
// then capped per zoom so a hexagon never falls under the tile server's own
// minimum), and the resolution named for "this view" is the rung the current
// zoom falls in.
import { computed } from "vue";

import {
	H3_RES_MAX,
	H3_RES_MIN,
	LADDER_OFFSET_MAX,
	LADDER_OFFSET_MIN,
	type LadderRung,
	hexPxAcross,
	ladderForOffset,
} from "@/lib/h3/ladder";

interface Props {
	/** The ladder offset in H3 steps: 0 is the base ladder, -1 one step coarser
	 * everywhere (the lab's own default). */
	offset: number;
	/** The map's current zoom, so the panel can name the resolution on screen. */
	zoom: number;
}

interface Emits {
	"update:offset": [offset: number];
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

/** The offset as the ladder reads it: rounded and clamped into the slider's own
 * range, so a value from anywhere else cannot ask for a rung the control does
 * not offer. */
const clampedOffset = computed(() =>
	Math.min(
		LADDER_OFFSET_MAX,
		Math.max(LADDER_OFFSET_MIN, Math.round(props.offset)),
	),
);

/** The rungs this offset draws, each already clamped to H3's range and capped by
 * its zoom band's cell-size guard. */
const rungs = computed<LadderRung[]>(() =>
	ladderForOffset(clampedOffset.value),
);

/** The rung the current zoom is in — the resolution the map is actually drawn
 * at. The last rung is the fallback: the ladder covers every zoom its own bands
 * name, and its last band reaches past any zoom a map view can hold. */
const rungNow = computed<LadderRung | undefined>(() =>
	rungs.value.find((rung) => props.zoom <= rung.maxZoom),
);

const resNow = computed(() => rungNow.value?.res ?? H3_RES_MIN);

/** How wide a hexagon of that resolution is on screen right now, in px. */
const pxNow = computed(() => hexPxAcross(resNow.value, props.zoom));

const signedOffset = computed(() =>
	clampedOffset.value > 0
		? `+${clampedOffset.value}`
		: String(clampedOffset.value),
);

/** The warning line: one rung of the ladder could not take the resolution the
 * offset asked for, so the grid stops moving before the slider does. Without it
 * the slider would look broken exactly where it is being careful. */
const capNote = computed(() => {
	const cell = rungs.value.some((rung) => rung.limit === "cell");
	const h3 = rungs.value.some((rung) => rung.limit === "h3");
	if (!cell && !h3) return "";
	const reasons: string[] = [];
	if (cell)
		reasons.push(
			"under half a pixel across, the tile server drops hexagons, so the ladder stops there",
		);
	if (h3)
		reasons.push(
			`H3 has no resolution past R${H3_RES_MAX} (its range is R${H3_RES_MIN}–R${H3_RES_MAX})`,
		);
	return `Capped: ${reasons.join("; ")}.`;
});

/** A px figure for a panel this narrow: one decimal under ten, whole numbers
 * above it, where the decimal is noise. */
function px(value: number): string {
	return value >= 10 ? value.toFixed(0) : value.toFixed(1);
}

function onOffset(event: Event): void {
	const next = (event.target as HTMLInputElement).valueAsNumber;
	if (Number.isNaN(next)) return;
	emit(
		"update:offset",
		Math.min(LADDER_OFFSET_MAX, Math.max(LADDER_OFFSET_MIN, Math.round(next))),
	);
}
</script>
